/**
 * @Time    : 2024/1/12 15:42
 * @Author  : tlovet
 * @Software: WebStorm
 * https://umijs.org/docs/max/request
 */

import { getAccessToken, redirectLogin } from '@/utils/login';
import type { RequestConfig, RequestOptions } from '@umijs/max';
import { getLocale } from '@umijs/max';
import { message, notification } from 'antd';

const codeMessage = {
  200: '服务器成功返回请求的数据。',
  201: '新建或修改数据成功。',
  202: '一个请求已经进入后台排队（异步任务）。',
  204: '删除数据成功。',
  400: '发出的请求有错误（参数错误），服务器没有进行新建或修改数据的操作。',
  401: '用户没有权限（令牌、用户名、密码错误）。',
  403: '用户得到授权，但是访问是被禁止的。',
  404: '发出的请求针对的是不存在的记录，服务器没有进行操作。',
  406: '请求的格式不可得。',
  410: '请求的资源被永久删除，且不会再得到的。',
  422: '当创建一个对象时，发生一个验证错误。',
  500: '服务器发生错误，请检查服务器。',
  502: '网关错误。',
  503: '服务不可用，服务器暂时过载或维护。',
  504: '网关超时。',
};

/**
 * @name RequestConfig 自定义请求参数
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 * https://axios-http.com/docs/req_config
 */
export const requestConfig: RequestConfig = {
  // 超时时长, 单位毫秒
  timeout: 50000,
  // 前缀，使用axios的配置项
  baseURL: '/api',
  // axios 请求自动携带 cookie
  withCredentials: true,
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出，处理200时success=false的所有响应；改为responseInterceptors
    // errorThrower: (response: APIResponse) => {
    //   // const { success = false, error = { message: '请求异常', code: 29001000 } } = response;
    //   const { success = false } = response;
    //   if (!success) {
    //     // eslint-disable-next-line
    //     throw { name: 'CustomError', response };
    //   }
    //   // // 使用下述方式，删除按钮点击时会报错
    //   // if (!success) {
    //   //   const errorResponse = new Error(error?.message);
    //   //   errorResponse.name = 'CustomError';
    //   //   errorResponse.response = response;
    //   //   throw errorResponse; // 抛出自制的错误
    //   // }
    // },
    // 错误接收及处理，处理非200时的所有响应和errorThrower抛出的自定义错误
    errorHandler: (errorResponse: any, opts: any) => {
      const { response } = errorResponse;
      if (errorResponse.name === 'CustomError') {
        // 处理200时success=false的所有响应
        // 后端请求成功，这时候判断自定义错误码
        const { error = { message: '请求异常', code: 29001000 } } = response as APIResponse;
        if (error.code === 29004001) {
          // 29004001错误，跳转到登陆页面
          message?.error(`登陆认证失败: ${error.message}`, 1, redirectLogin);
        } else {
          // 跳过报错，一般是不弹报错信息
          if (opts?.skipErrorHandler) throw errorResponse;
          // 非29004001错误，弹窗提示
          message?.error(error.message);
        }
      } else if (response) {
        const { status = 200, statusText = '' } = response || {};
        // @ts-ignore
        const errorText = codeMessage[status] || statusText;
        // 基于http返回码判断
        switch (status) {
          case 401:
            message.error(`登陆认证失败: ${errorText}`, 1, redirectLogin);
            break;
          default:
            notification.error({
              message: `请求错误 ${status}: ${errorResponse?.request?.responseURL}`,
              description: errorText,
            });
            break;
        }
      } else if (errorResponse?.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message?.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message?.error('Request error, please retry.');
      }
      throw errorResponse;
    },
  },

  // 请求拦截器，以下两种写法都可以
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      // 所有请求增加参数apiVersion
      const accessToken = getAccessToken();
      return {
        ...config,
        headers: {
          ...config.headers,
          'Access-Token': accessToken as string,
          locale: getLocale(),
        },
      };
      // const url = config?.url?.concat('?apiVersion = 1.0');
      // return { ...config, url };
    },
    // (url: string, options: RequestOptions) => {
    //   // 拦截请求配置，进行个性化处理。
    //   // const url = config?.url?.concat('?apiVersion = 1.0');
    //   // return { ...config, url };
    //   return {
    //     url,
    //     options: {
    //       ...options,
    //       params: { ...options.params, apiVersion: '1.0' },
    //       interceptors: true,
    //     },
    //   };
    // },
  ],

  // 响应拦截器
  responseInterceptors: [
    (axiosResponse) => {
      const opts = axiosResponse?.config;
      // 增加code，兼容之前的旧接口标准，比如Plus
      const {
        // @ts-ignore
        code = 20000,
        success = true,
        error = { message: '请求异常', code: 29001000 },
      } = axiosResponse?.data as APIResponse;
      // code用来兼容旧接口，新旧接口有一个返回失败，就开始判断错误
      if (!success || code !== 20000) {
        if (error.code === 29004001) {
          // 29004001错误，跳转到登陆页面
          message?.error(`登陆认证失败: ${error.message}`, 1, redirectLogin);
        } else {
          // 增加扩展参数skipErrorHandler，是否跳过报错
          // @ts-ignore
          if (!opts?.skipErrorHandler) {
            // 非29004001错误，弹窗提示
            message?.error(error.message);
          }
        }
      }

      // 兼容Falcon的接口，如果返回头中有access-control-allow-headers，则认为是请求的Falcon
      if (axiosResponse?.headers.hasOwnProperty('access-control-allow-headers')) {
        return { ...axiosResponse, success: true };
      }
      return axiosResponse;
    },
  ],
};
